/**
 * ThemeProvider component - Simple local theme management
 */

'use client';

import React, { useMemo, createContext, useEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, useMediaQuery, PaletteMode } from '@mui/material';
import { createRunnersHubTheme } from '../config/theme';

// Theme context for compatibility with existing components
type ThemeMode = 'system' | 'light' | 'dark';
interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

export const ThemeModeContext = createContext<ThemeContextType>({
  mode: 'system',
  setMode: () => {},
});

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Detect system/browser dark mode
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Load theme mode from localStorage or default to system
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode');
    return (saved as ThemeMode) || 'system';
  });

  // Save theme mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Resolve actual palette mode
  const resolvedMode: PaletteMode = useMemo(() => {
    if (mode === 'system') return prefersDarkMode ? 'dark' : 'light';
    return mode;
  }, [mode, prefersDarkMode]);
  
  const theme = useMemo(() => createRunnersHubTheme(resolvedMode), [resolvedMode]);

  const contextValue: ThemeContextType = {
    mode,
    setMode
  };

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
